import { useCallback, useEffect, useMemo, useState } from "react";
import { Edit3, KeyRound, Plus, Save, Trash2, X } from "lucide-react";
import { api, apiErrToStr } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const CATEGORIES = [
  "Lista negra", "Central de atendimento", "Encaminhamento de chamadas",
  "Recursos de chamada", "Servicos de teste", "Gravacoes e comunicados", "Outros",
];

const emptyForm = (tenantId = "") => ({
  tenant_id: tenantId, nome: "", tipo: "custom", codigo: "", categoria: "Recursos de chamada",
  descricao: "", implementation: "catalog", enabled: true,
});

export default function FeatureCodes() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";
  const [rows, setRows] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm(user?.tenant_id || ""));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [codes, tenantRows] = await Promise.all([
        api.get("/feature-codes"),
        isSuperAdmin ? api.get("/tenants") : Promise.resolve({ data: [] }),
      ]);
      setRows(codes.data || []);
      setTenants(tenantRows.data || []);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => { load(); }, [load]);

  const tenantMap = useMemo(() => Object.fromEntries(tenants.map((tenant) => [tenant.id, tenant.nome])), [tenants]);
  const visibleRows = useMemo(() => {
    if (!isSuperAdmin || selectedTenant === "all") return rows;
    return rows.filter((row) => row.tenant_id === selectedTenant);
  }, [isSuperAdmin, rows, selectedTenant]);
  const grouped = useMemo(() => visibleRows.reduce((result, row) => {
    const category = row.categoria || "Outros";
    (result[category] ||= []).push(row);
    return result;
  }, {}), [visibleRows]);

  const openForm = (row = null) => {
    setEditing(row);
    setError("");
    setForm(row ? { ...row } : emptyForm(user?.tenant_id || ""));
    setShowForm(true);
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true); setError("");
    try {
      const payload = { ...form, codigo: String(form.codigo || "").replace(/[^0-9*#]/g, "") };
      if (!payload.codigo) throw new Error("Informe o codigo de facilidade.");
      if (editing) await api.put(`/feature-codes/${editing.id}`, payload);
      else await api.post("/feature-codes", payload);
      setShowForm(false); setEditing(null); await load();
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Excluir o codigo ${row.codigo}?`)) return;
    try { await api.delete(`/feature-codes/${row.id}`); await load(); }
    catch (err) { window.alert(apiErrToStr(err?.response?.data?.detail) || err.message); }
  };

  return (
    <div className="space-y-5" data-testid="feature-codes-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Codigos de servico do IPBX</div>
          <h1 className="text-2xl font-semibold tracking-tight">Codigos de facilidade</h1>
          <p className="mt-1 text-xs text-[color:var(--text-secondary)]">Consulte e personalize os codigos usados pelos ramais.</p>
        </div>
        <button className="sbc-btn sbc-btn-primary" onClick={() => openForm()}><Plus size={14} /> Adicionar codigo</button>
      </div>

      <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-elevated)] px-4 py-3 text-xs text-[color:var(--text-secondary)]">
        Alteracoes ficam pendentes ate aplicar a configuracao do IPBX. Codigos ativos nao podem ser duplicados dentro do mesmo cliente.
      </div>

      {isSuperAdmin && <div className="flex max-w-xl items-center gap-3">
        <label className="sbc-label whitespace-nowrap" htmlFor="feature-code-tenant-filter">Exibir cliente</label>
        <select id="feature-code-tenant-filter" className="sbc-input" value={selectedTenant} onChange={(event) => setSelectedTenant(event.target.value)}>
          <option value="all">Todos os clientes</option>
          {tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.nome}</option>)}
        </select>
      </div>}

      <div className="sbc-card overflow-hidden">
        {loading ? <div className="py-10 text-center text-sm">Carregando...</div> : !visibleRows.length ? <div className="py-10 text-center text-sm text-[color:var(--text-muted)]">Nenhum codigo cadastrado para este cliente.</div> : (
          <div className="overflow-auto">
            <table className="sbc-table">
              <thead><tr>{isSuperAdmin && <th>Cliente</th>}<th>Categoria</th><th>Funcao</th><th>Codigo</th><th>Implementacao</th><th>Status</th><th className="text-right">Acoes</th></tr></thead>
              <tbody>{Object.entries(grouped).flatMap(([category, categoryRows]) => categoryRows.map((row) => (
                <tr key={row.id}>
                  {isSuperAdmin && <td>{tenantMap[row.tenant_id] || row.tenant_id}</td>}
                  <td><span className="sbc-tag">{category}</span></td>
                  <td><div className="font-medium">{row.nome}</div><div className="text-[10px] text-[color:var(--text-muted)]">{row.descricao || "-"}</div></td>
                  <td className="font-mono text-sm">{row.codigo}</td>
                  <td>{row.implementation === "native" ? "Nativa" : "Catalogo"}</td>
                  <td>{row.enabled !== false ? <span className="sbc-tag tag-online">Ativo</span> : <span className="sbc-tag tag-offline">Inativo</span>}</td>
                  <td className="text-right"><div className="inline-flex gap-1"><button className="sbc-btn" title="Editar" onClick={() => openForm(row)}><Edit3 size={12} /></button><button className="sbc-btn sbc-btn-danger" title="Excluir" onClick={() => remove(row)}><Trash2 size={12} /></button></div></td>
                </tr>
              )))}</tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-4" onClick={() => setShowForm(false)}>
        <div className="sbc-card max-h-[92vh] w-full max-w-2xl overflow-auto p-6" onClick={(event) => event.stopPropagation()}>
          <div className="mb-5 flex items-start justify-between"><div><div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{editing ? "Editar" : "Novo"}</div><h2 className="flex items-center gap-2 text-lg font-semibold"><KeyRound size={18} /> Codigo de facilidade</h2></div><button className="sbc-btn" onClick={() => setShowForm(false)}><X size={14} /></button></div>
          <form onSubmit={save} className="space-y-4">
            {isSuperAdmin && <label><span className="sbc-label">Cliente</span><select className="sbc-input mt-1" value={form.tenant_id || ""} onChange={(event) => setForm({ ...form, tenant_id: event.target.value })} required><option value="">-- selecione --</option>{tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.nome}</option>)}</select></label>}
            <div className="grid gap-4 md:grid-cols-2">
              <label><span className="sbc-label">Nome</span><input className="sbc-input mt-1" value={form.nome || ""} onChange={(event) => setForm({ ...form, nome: event.target.value })} required /></label>
              <label><span className="sbc-label">Codigo</span><input className="sbc-input mt-1 font-mono" value={form.codigo || ""} onChange={(event) => setForm({ ...form, codigo: event.target.value })} placeholder="Ex: *72 ou #1" required /></label>
              <label><span className="sbc-label">Categoria</span><select className="sbc-input mt-1" value={form.categoria || "Outros"} onChange={(event) => setForm({ ...form, categoria: event.target.value })}>{CATEGORIES.map((category) => <option key={category}>{category}</option>)}</select></label>
              <label><span className="sbc-label">Tipo interno</span><input className="sbc-input mt-1" value={form.tipo || "custom"} onChange={(event) => setForm({ ...form, tipo: event.target.value })} placeholder="Ex: pickup, park" /></label>
              <label><span className="sbc-label">Implementacao</span><select className="sbc-input mt-1" value={form.implementation || "catalog"} onChange={(event) => setForm({ ...form, implementation: event.target.value })}><option value="native">Nativa do IPBX</option><option value="catalog">Catalogo</option></select></label>
              <label className="flex items-end gap-2 pb-2"><input type="checkbox" checked={form.enabled !== false} onChange={(event) => setForm({ ...form, enabled: event.target.checked })} /><span className="text-sm">Ativo</span></label>
            </div>
            <label><span className="sbc-label">Descricao</span><textarea className="sbc-input mt-1 min-h-20" value={form.descricao || ""} onChange={(event) => setForm({ ...form, descricao: event.target.value })} /></label>
            {error && <div className="border border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.08)] px-3 py-2 text-xs text-[color:var(--accent-red)]">! {error}</div>}
            <div className="flex justify-end gap-2"><button type="button" className="sbc-btn" onClick={() => setShowForm(false)}>Cancelar</button><button type="submit" className="sbc-btn sbc-btn-primary" disabled={saving}><Save size={13} /> {saving ? "Salvando..." : "Salvar"}</button></div>
          </form>
        </div>
      </div>}
    </div>
  );
}
